<template>
  <div>
    <div
      class="plan-cards"
      :class="{ 'is-compact': compact }"
    >
      <div
        v-for="plan in plans"
        :key="plan.pk"
        class="plan-card"
      >
        <div>
          <span
            class="icon-static margin-bottom-sm"
            :class="[compact ? 'xl' : 'xxl', plan.slug]"
          />
          <h3 class="title-wide is-3 mb-4">
            {{ getPlanName(plan) }}
          </h3>
          <p
            class="text color-bluegrey1 mt-0 mb-0"
            :class="{ xl: !compact }"
          >
            {{ plan.description }}
          </p>
        </div>
        <h3
          class="title-lowercase mb-1 mt-5"
          :class="compact ? 'is-5' : 'is-4'"
        >
          {{ plan.amount|formattedDbAmount('', 2, plan.currency) }} <small>/ {{ plan.interval }}</small>
        </h3>
        <div class="plan-card-action">
          <button
            class="button is-primary is-large is-rounded"
            @click="handlePlanSelect(plan)"
          >
            {{ selectButtonText }}
          </button>
        </div>
        <ul class="plan-card-details">
          <li
            v-for="item in planDetails(plan)"
            :key="item"
          >
            {{ item }}
          </li>
        </ul>
      </div>
    </div>

    <b-modal
      :active.sync="showAlreadySubscribedModal"
      :width="450"
    >
      <div
        class="modal-card"
        style="width: auto;"
      >
        <header
          class="modal-card-head is-block "
          style="padding: 32px 32px 8px 32px;"
        >
          <span class="icon-static xl small-check margin-bottom-lg" />
          <h3 class="title is-3">
            It looks like you already have a subscription!
          </h3>
        </header>
        <section
          class="modal-card-body"
          style="padding: 12px 36px 36px 36px;"
        >
          <p>
            If you're trying to upgrade please contact us at <a href="mailto:hello@sonata.media">hello@sonata.media</a>.
          </p>
        </section>
        <footer class="modal-card-foot is-block">
          <button
            class="button is-fullwidth is-secondary is-large"
            @click.prevent="showAlreadySubscribedModal = false"
          >
            Close
          </button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { SUBSCRIBE_FLOW_SWITCH } from '@/utils/subscribeFlow.js'

// One card per plan, stacked on phones and tablets and side by side from desktop width.
// `compact` is the shorter version used in the cart and the plan selection modal.
export default {
  name: 'PricingPlanCardList',

  props: {
    plans: {
      type: Array,
      required: true
    },

    compact: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      showAlreadySubscribedModal: false
    }
  },

  computed: {
    isLoggedIn: function () {
      return this.$store.getters['auth/isLoggedIn']
    },

    trialMode: function () {
      return this.$store.getters['subscriptions/createSubscriptionWithTrial']
    },

    selectButtonText: function () {
      if (this.trialMode) {
        return 'Start free trial'
      }
      return 'Select'
    }
  },

  methods: {
    handlePlanSelect: function (plan) {
      if (this.isLoggedIn && this.$store.getters['subscriptions/currentSubscription']) {
        this.showAlreadySubscribedModal = true
      } else if (this.$store.getters['waffle/isFeatureEnabled'](SUBSCRIBE_FLOW_SWITCH)) {
        // Straight to paying for the chosen plan; logged-out visitors create an
        // account first and are linked on to it afterwards.
        const payment = { name: 'subscribe-payment', query: { plan: String(plan.pk) } }
        this.$store.dispatch('subscriptions/hidePlanSelectionModal')
        if (this.isLoggedIn) {
          this.$router.push(payment)
        } else {
          this.$store.dispatch('auth/setRedirectTo', this.$router.resolve(payment).route.fullPath)
          this.$store.dispatch('user/showSignupModal')
        }
      } else {
        this.$store.dispatch('subscriptions/selectPlan', plan)
          .then(() => {
            this.$store.dispatch('subscriptions/hidePlanSelectionModal')
              .then(() => {
                if (this.isLoggedIn) {
                  this.$router.push({ name: 'subscription-checkout' })
                } else {
                  this.$store.dispatch('user/showSignupModal')
                }
              })
          })
      }
    },

    getPlanName: function (plan) {
      const parts = []
      if (this.trialMode) {
        parts.push('Try')
        parts.push(' ')
      }
      parts.push(plan.product_name)
      return parts.join('')
    },

    planDetails: function (plan) {
      const details = plan.details ? plan.details.split('\n') : []
      return this.compact ? details.slice(0, 3) : details
    }
  }
}
</script>

<style scoped lang="scss">
@import "../assets/scss/_variables.scss";

.plan-cards {
  display: grid;
  gap: 1rem;
  margin-bottom: 1.5rem;
}
.plan-card {
  display: grid;
  align-content: start;
  row-gap: 0;
  background: $bluegrey7;
  padding: 1.5rem 1rem;
}
.plan-card-action {
  margin-top: 0.75rem;
}
.plan-card-details {
  border-top: 1px solid $grey-lighter;
  margin-top: 1.25rem;
  padding-top: 1.25rem;
}

// Side by side, each card's sections share a row with the matching section of the other
// cards, so prices, buttons and details line up whatever the description length.
@media screen and (min-width: $desktop) {
  .plan-cards {
    grid-auto-columns: minmax(0, 1fr);
    grid-auto-flow: column;
    grid-template-rows: repeat(4, auto);
  }
  .plan-card {
    grid-row: span 4;
    grid-template-rows: subgrid;
  }
}
</style>
